import { BRAND } from "constants/brand";
import { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import useVendors from "hooks/useVendors";
import { api } from "api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import { Ring } from "@uiball/loaders";
import { Toast } from "primereact/toast";
import { useSelector } from "react-redux";
import { toHHmm } from "util/time";
import "./vendorTimes.scss";
import TimeField from "components/Ui/TimeField/TimeField";
import { WEEKDAY_OPTIONS } from "utils/dateFormat";
// The weekday names come from the one list in utils/dateFormat.js.
const DAYS = WEEKDAY_OPTIONS;

export default function VendorTimes() {
    const [schedule, setSchedule] = useState({});
    const { loading, updateVendorSchudle } = useVendors();
    const [isLoading, setLoading] = useState(false);
    const toast = useRef(null);
    const adminInfo = useSelector((state) => state.auth.userData);

    useEffect(() => {
        if (!adminInfo.vendorId) return;

        setLoading(true);
        api.get(`vendor/weekly-schedule/${adminInfo.vendorId}`)
            .then((res) => {
                const data = {};
                res.data.schedule.forEach((d) => {
                    if (!d.openTime && !d.closeTime && !d.isClosed) return;
                    data[d.day] = {
                        openTime: toHHmm(d.openTime),
                        closeTime: toHHmm(d.closeTime),
                        isClosed: d.isClosed,
                    };
                });
                setSchedule(data);
            })
            .finally(() => setLoading(false));
    }, [adminInfo.vendorId]);

    const updateDay = (day, field, value) => {
        setSchedule((prev) => ({
            ...prev,
            [day]: {
                ...prev[day],
                [field]: value,
                isClosed: field === "isClosed" ? value : false,
                remove: false,
            },
        }));
    };

    /** Mark a day for removal (deletes DB row → admin-managed) */
    const removeDay = (day) => {
        setSchedule((prev) => ({
            ...prev,
            [day]: { remove: true },
        }));
    };

    /** Re-add a removed day with empty times */
    const addDay = (day) => {
        setSchedule((prev) => ({
            ...prev,
            [day]: { openTime: "", closeTime: "", isClosed: false, remove: false },
        }));
    };

    const save = async () => {
        try {
            await updateVendorSchudle(adminInfo.vendorId, { schedule });
        } catch (err) {
            toast.current?.show({
                severity: "error",
                summary: "خطأ",
                detail: err.message,
                life: 4000,
            });
        }
    };

    return (
        <div className="items">
            {loading ? (
                <div className="loading-spinner center">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                </div>
            ) : null}
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />
            <div style={{ padding: "20px" }}>
                {isLoading
                    ? "جاري التحميل..."
                    : DAYS.map((day, idx) => {
                          const entry = schedule[day.key];
                          const isRemoved = !entry || entry.remove;
                          return (
                              // A SURFACE PAINTED INLINE CANNOT BE THEMED. This
                              // row carried `backgroundColor: "#f8f9fa"/"#fff"`
                              // in its style prop, and an inline style beats
                              // every stylesheet rule at any specificity — so no
                              // `.dark` rule could ever reach it and the whole
                              // week stayed white on a dark page. It is also
                              // invisible to any audit of document.styleSheets,
                              // because it never reaches a stylesheet.
                              //
                              // `.surface-card` and `.surface-inset` carry their
                              // own dark rules (index.scss); the striping is the
                              // difference between the two.
                              <div
                                  key={day.key}
                                  className={`vendor-times__row ${
                                      idx % 2 === 0 ? "surface-inset" : "surface-card"
                                  }`}
                              >
                                  <strong className="vendor-times__day">{day.label}</strong>

                                  <div className="vendor-times__divider" />
                                  {isRemoved ? (
                                      <>
                                          <span
                                              className="text-muted"
                                              style={{
                                                  flex: 1,
                                                  fontStyle: "italic",
                                                  fontSize: "0.875rem",
                                                  paddingRight: "4px",
                                              }}
                                          >
                                              لم يتم التعيين
                                          </span>
                                          <Button
                                              variant="outline-success"
                                              size="sm"
                                              onClick={() => addDay(day.key)}
                                              title="تعيين أوقات"
                                              style={{ flexShrink: 0 }}
                                          >
                                              <FontAwesomeIcon icon={faPlus} className="me-1" />{" "}
                                              تعيين
                                          </Button>
                                      </>
                                  ) : (
                                      <>
                                          <TimeField
                                              disabled={entry?.isClosed}
                                              value={entry?.openTime || ""}
                                              onChange={(value) =>
                                                  updateDay(day.key, "openTime", value)
                                              }
                                          />

                                          <span
                                              style={{
                                                  flexShrink: 0,
                                                  // Inherit: a literal grey
                                                  // disappears on a dark row.
                                                  fontSize: "0.875rem",
                                              }}
                                          >
                                              إلى
                                          </span>

                                          <TimeField
                                              disabled={entry?.isClosed}
                                              value={entry?.closeTime || ""}
                                              onChange={(value) =>
                                                  updateDay(day.key, "closeTime", value)
                                              }
                                          />

                                          <label
                                              style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "6px",
                                                  flexShrink: 0,
                                                  marginBottom: 0,
                                                  cursor: "pointer",
                                                  userSelect: "none",
                                                  fontSize: "0.875rem",
                                                  // Only the ON state keeps a
                                                  // literal colour, because it
                                                  // is the brand and reads on
                                                  // both grounds. The OFF state
                                                  // inherits, so the theme picks
                                                  // it — it was #6c757d, which
                                                  // is invisible on a dark row.
                                                  color: entry?.isClosed ? BRAND : undefined,
                                                  fontWeight: entry?.isClosed ? 600 : 400,
                                              }}
                                          >
                                              <input
                                                  className="form-check-input"
                                                  type="checkbox"
                                                  style={{ marginTop: 0, cursor: "pointer" }}
                                                  checked={entry?.isClosed || false}
                                                  onChange={(e) =>
                                                      updateDay(
                                                          day.key,
                                                          "isClosed",
                                                          e.target.checked,
                                                      )
                                                  }
                                              />
                                              مغلق
                                          </label>
                                          <Button
                                              variant="outline-danger"
                                              size="sm"
                                              onClick={() => removeDay(day.key)}
                                              title="إزالة الأوقات"
                                              style={{ flexShrink: 0, marginRight: "auto" }}
                                          >
                                              <FontAwesomeIcon icon={faTrash} />
                                          </Button>
                                      </>
                                  )}
                              </div>
                          );
                      })}
                <div style={{ marginTop: "20px" }}>
                    <Button style={{ width: "200px" }} variant="success" onClick={save}>
                        حفظ
                    </Button>
                </div>
            </div>
        </div>
    );
}
